/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: the top of a program: cover, name, plain facts and the day-to-day actions.
 */
import React from "react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PrivateIcon,
} from "@lemonadetraining/web-ui";
import { IconCopy, IconDotsVertical, IconPlus } from "@tabler/icons-react";
import { CoverHero } from "../CoverArt";
import { membersLabel, programPriceLabel, programTypeLabel } from "../../lib/programLabels";
import type { ProgramDoc, RunsAs } from "./programTypes";

export interface ProgramHeaderProps {
  program: ProgramDoc;
  runs: RunsAs;
  canManage: boolean;
  /** "by Aino Laine"; undefined when unknown or it is the viewer's own. */
  byline?: string;
  onAddWorkout: () => void;
  onCopyWeek: () => void;
  onTogglePublic?: () => void;
  onOpenSettings: () => void;
  busy?: boolean;
}

export function ProgramHeader({
  program,
  runs,
  canManage,
  byline,
  onAddWorkout,
  onCopyWeek,
  onTogglePublic,
  onOpenSettings,
  busy = false,
}: ProgramHeaderProps) {
  const type = programTypeLabel(program);
  const price = programPriceLabel(program);
  const members = program.subscribers?.length ?? 0;
  const copyLabel = runs === "daily" ? "Copy this week…" : runs === "weeks" ? "Copy a week…" : null;

  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-start" data-testid="program-header">
      <CoverHero url={program.coverArtURL} />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight text-balance">{program.name}</h1>
          {program.isPT && (
            <Badge variant="outline" className="gap-1">
              <PrivateIcon className="size-3" aria-hidden="true" />
              1-to-1 (PT)
            </Badge>
          )}
          {!program.active && <Badge variant="outline">Archived</Badge>}
        </div>
        <p className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {type && <span>{type}</span>}
          {price && <span>{price}</span>}
          {!program.isPT && <span>{membersLabel(members)}</span>}
          {program.landingPage && program.active && !program.isPT && (
            <span>On your public page</span>
          )}
          {byline && <span>{byline}</span>}
        </p>
        {canManage && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button onClick={onAddWorkout} disabled={busy} data-testid="program-add-workout">
              <IconPlus className="size-4" aria-hidden="true" />
              Add workout
            </Button>
            {copyLabel && (
              <Button
                variant="outline"
                onClick={onCopyWeek}
                disabled={busy}
                data-testid="program-copy-week"
              >
                <IconCopy className="size-4" aria-hidden="true" />
                {copyLabel}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" aria-label="More" data-testid="program-more">
                  <IconDotsVertical className="size-4" aria-hidden="true" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {onTogglePublic && (
                  <DropdownMenuItem onSelect={onTogglePublic}>
                    {program.landingPage ? "Hide from public page" : "Show on public page"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={onOpenSettings}>Settings</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </header>
  );
}
